/**
 * 🖼️ DOCUMENT VIEWER - Component for displaying individual verification documents
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Skeleton,
} from '@mui/material';
import {
  ZoomIn,
  Download,
  Visibility,
  Error,
  CheckCircle,
  Warning,
} from '@mui/icons-material';

interface DocumentViewerProps {
  imageUrl?: string;
  title: string;
  documentType?: 'nin' | 'selfie' | 'license_front' | 'license_back';
  isLoading?: boolean;
  onViewFull?: () => void;
  onDownload?: () => void;
  showStatus?: boolean;
  status?: 'approved' | 'pending' | 'rejected';
  width?: number;
  height?: number;
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  imageUrl,
  title,
  documentType,
  isLoading = false,
  onViewFull,
  onDownload,
  showStatus = true,
  status = 'pending',
  width = 200,
  height = 150
}) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  const getStatusIcon = () => {
    switch (status) {
      case 'approved':
        return <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />;
      case 'rejected':
        return <Error sx={{ color: 'error.main', fontSize: 16 }} />;
      default:
        return <Warning sx={{ color: 'warning.main', fontSize: 16 }} />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'warning';
    }
  };

  const getDocumentTypeLabel = (type?: string) => {
    switch (type) {
      case 'nin': return 'NIN Document';
      case 'selfie': return 'Driver Selfie';
      case 'license_front': return 'License Front';
      case 'license_back': return 'License Back';
      default: return title;
    }
  };

  if (isLoading) {
    return (
      <Card sx={{ width, height: height + 60 }}>
        <CardContent sx={{ p: 1 }}>
          <Skeleton variant="text" sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={height} />
        </CardContent>
      </Card>
    );
  }

  if (!imageUrl) {
    return (
      <Card sx={{ width, height: height + 60, border: '2px dashed', borderColor: 'grey.300' }}>
        <CardContent sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Error sx={{ color: 'grey.400', fontSize: 48, mb: 1 }} />
          <Typography variant="caption" color="text.secondary" align="center">
            {getDocumentTypeLabel(documentType)}
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center">
            Not uploaded
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ 
      width, 
      height: height + 60,
      position: 'relative',
      '&:hover .image-actions': {
        opacity: 1
      }
    }}>
      <CardContent sx={{ p: 1 }}>
        {/* Header with title and status */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
            {getDocumentTypeLabel(documentType)}
          </Typography>
          {showStatus && (
            <Tooltip title={`Status: ${status}`}>
              {getStatusIcon()}
            </Tooltip>
          )}
        </Box>

        {/* Image container */}
        <Box sx={{ 
          position: 'relative', 
          width: '100%', 
          height, 
          overflow: 'hidden', 
          borderRadius: 1,
          backgroundColor: 'grey.100'
        }}>
          {imageLoading && (
            <Box sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              width: '100%', 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              zIndex: 1
            }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {imageError ? (
            <Box sx={{ 
              width: '100%', 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              bgcolor: 'error.light',
              color: 'error.contrastText'
            }}>
              <Error sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="caption">
                Failed to load
              </Typography>
            </Box>
          ) : (
            <img
              src={imageUrl}
              alt={getDocumentTypeLabel(documentType)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: imageLoading ? 'none' : 'block'
              }}
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageLoading(false);
                setImageError(true);
              }}
            />
          )}

          {/* Hover actions */}
          {!imageError && (
            <Box 
              className="image-actions"
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                opacity: 0,
                transition: 'opacity 0.2s ease-in-out',
                display: 'flex',
                gap: 0.5,
                bgcolor: 'rgba(0,0,0,0.6)',
                borderRadius: 1,
                p: 0.5
              }}
            >
              {onViewFull && (
                <Tooltip title="View Full Size">
                  <IconButton 
                    size="small" 
                    onClick={onViewFull}
                    sx={{ color: 'white', p: 0.5 }}
                  >
                    <ZoomIn fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              
              {onDownload && (
                <Tooltip title="Download">
                  <IconButton 
                    size="small" 
                    onClick={onDownload}
                    sx={{ color: 'white', p: 0.5 }}
                  >
                    <Download fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {/* Status indicator */}
        {showStatus && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: `${getStatusColor()}.main`,
                fontSize: '0.6rem',
                textTransform: 'uppercase',
                fontWeight: 600
              }}
            >
              {status}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};